import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './UnidadesOrgModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import Tabs from './UnidadesOrgTabs';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Unidades Organizacionales";
const endpoint = ENDPOINTS.unidadesOrg;
const endpointById = ENDPOINTS.unidadesOrgById;
const modelo = Models.unidadesOrg;

const maps = {
  mapCreate: (f) => ({
    idZonaGeografica: Number(f.idZonaGeografica) || 0,
    idDireccion: Number(f.idDireccion) || 0,
    clave: f.clave,
    nombre: f.nombre,
    idTipoUnidadOrganizacional: Number(f.idTipoUnidadOrganizacional) || 0,
    descripcion: f.descripcion,
  }),
  mapUpdate: (id, f) => ({
    idUnidadOrganizacional: id,
    idZonaGeografica: Number(f.idZonaGeografica) || 0,
    idDireccion: Number(f.idDireccion) || 0,
    clave: f.clave,
    nombre: f.nombre,
    idTipoUnidadOrganizacional: Number(f.idTipoUnidadOrganizacional) || 0,
    descripcion: f.descripcion,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  // 👉 catálogos para reemplazar IDs por valores
  const [zonas, setZonas] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [tiposUnidadOrg, setTiposUnidadOrg] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresasUnidadesOrg, setEmpresasUnidadesOrg] = useState([]);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resUnidades, resZonas, resDirecciones, resTipos, resEmpresas, resRel] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS.zonasGeograficas),
        axios.get(ENDPOINTS.direcciones),
        axios.get(ENDPOINTS.tipoUnidadOrg),
        axios.get(ENDPOINTS.empresas),
        axios.get(ENDPOINTS.empresasUnidadesOrg),
      ]);

      // Unidades organizacionales normalizadas
      setRows((resUnidades.data || []).map((x) => modelo(x)));

      // Zonas
      const rawZonas = Array.isArray(resZonas.data)
        ? resZonas.data
        : Array.isArray(resZonas.data?.data)
          ? resZonas.data.data
          : [];
      setZonas(rawZonas.map((z) => Models.zonasGeograficas(z)));

      // Direcciones
      const rawDirecciones = Array.isArray(resDirecciones.data)
        ? resDirecciones.data
        : Array.isArray(resDirecciones.data?.data)
          ? resDirecciones.data.data
          : [];
      setDirecciones(rawDirecciones.map((d) => Models.direcciones(d)));

      // Tipos unidad org
      const rawTipos = Array.isArray(resTipos.data)
        ? resTipos.data
        : Array.isArray(resTipos.data?.data)
          ? resTipos.data.data
          : [];
      setTiposUnidadOrg(rawTipos.map((t) => Models.tipoUnidadOrg(t)));

      // Empresas
      setEmpresas((resEmpresas.data || []).map(Models.empresas));

      // Relaciones
      setEmpresasUnidadesOrg((resRel.data || []).map(Models.empresasUnidadesOrg));

    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cargar información. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ✅ índices para búsquedas rápidas por ID
  const zonasIndex = useMemo(() => {
    const map = {};
    zonas.forEach((z) => {
      if (!z) return;

      // intentamos varias formas posibles de id
      const id =
        z.idZonaGeografica ??
        z.IdZonaGeografica ??
        z.idZona ??
        z.IdZona;

      if (id != null) {
        map[id] = z;
      }
    });
    return map;
  }, [zonas]);


  const direccionesIndex = useMemo(() => {
    const map = {};
    direcciones.forEach((d) => {
      if (d && d.idDireccion != null) {
        map[d.idDireccion] = d;
      }
    });
    return map;
  }, [direcciones]);

  const tiposUnidadOrgIndex = useMemo(() => {
    const map = {};
    tiposUnidadOrg.forEach((t) => {
      const id =
        t.idTipoUnidadOrganizacional ??
        t.idTipoUndiadOrganizacional ??
        t.idTipoUnidadOrg;
      if (id != null) map[id] = t;
    });
    return map;
  }, [tiposUnidadOrg]);

  const empresasIndex = useMemo(() => {
    const map = {};
    empresas.forEach((e) => {
      if (e && e.idEmpresa != null) map[e.idEmpresa] = e;
    });
    return map;
  }, [empresas]);

  const relEmpresaIndex = useMemo(() => {
    const map = {};
    empresasUnidadesOrg.forEach((rel) => {
      if (rel && rel.idUnidadOrganizacional != null) map[rel.idUnidadOrganizacional] = rel.idEmpresa;
    });
    return map;
  }, [empresasUnidadesOrg]);

  // 🧩 helpers de formato

  const formatEmpresa = useCallback((r) => {
    const idEmpresa = relEmpresaIndex[r.idUnidadOrganizacional];
    if (!idEmpresa) return 'Sin Empresa';
    const emp = empresasIndex[idEmpresa];
    return normalizeValue(emp ? emp.nombre : `Empresa #${idEmpresa}`);
  }, [relEmpresaIndex, empresasIndex]);

  // 🔹 AHORA ZONA TAMBIÉN SALE EN TEXTO (clave - nombre)
  const formatZona = useCallback((r) => {
    // id de la zona en la fila de UnidadOrg (defensivo)
    const idZona =
      r.idZonaGeografica ??
      r.IdZonaGeografica ??
      r.idZona ??
      r.IdZona;

    const zona = zonasIndex[idZona];
    if (!zona) {
      return 'Not Found';
    }

    const clave =
      zona.clave ??
      zona.Clave ??
      zona.codigo ??
      zona.Codigo ??
      '';

    const nombre =
      zona.nombre ??
      zona.Nombre ??
      zona.descripcion ??
      zona.Descripcion ??
      '';

    const label =
      (clave && nombre
        ? `${clave} - ${nombre}`
        : nombre || clave) || `Zona ${idZona}`;

    return normalizeValue(label);
  }, [zonasIndex]);


  const formatDireccion = useCallback((r) => {
    const dir = direccionesIndex[r.idDireccion];
    if (!dir) return normalizeValue(r.idDireccion);

    const parts = [];

    const loc = [dir.estado, dir.municipio, dir.colonia].filter(Boolean).join(', ');
    if (loc) parts.push(loc);

    if (dir.calle) {
      let calle = dir.calle;
      if (dir.numeroExterior) calle += ` ${dir.numeroExterior}`;
      parts.push(calle);
    }

    return normalizeValue(parts.join(' · '));
  }, [direccionesIndex]);

  const formatTipoUnidadOrg = useCallback((r) => {
    const idTipo =
      r.idTipoUnidadOrganizacional ?? r.idTipoUndiadOrganizacional;
    const tipo = tiposUnidadOrgIndex[idTipo];
    if (!tipo) return normalizeValue(idTipo);

    const label =
      (tipo.clave && tipo.nombre
        ? `${tipo.clave}`
        : tipo.nombre || tipo.clave) || `Tipo ${idTipo}`;
    return normalizeValue(label);
  }, [tiposUnidadOrgIndex]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const clave = (r.clave || '').toLowerCase();
      const nombre = (r.nombre || '').toLowerCase();
      const zona = (formatZona(r) || '').toLowerCase();
      const dir = (formatDireccion(r) || '').toLowerCase();
      const tipo = (formatTipoUnidadOrg(r) || '').toLowerCase();
      const empresa = (formatEmpresa(r) || '').toLowerCase();
      return (
        clave.includes(q) ||
        nombre.includes(q) ||
        zona.includes(q) ||
        dir.includes(q) ||
        tipo.includes(q) ||
        empresa.includes(q)
      );
    });
  }, [rows, query, formatZona, formatDireccion, formatTipoUnidadOrg, formatEmpresa]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idUnidadOrganizacional === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al eliminar. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      let targetId = selectedId;

      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        targetId = res?.data?.idUnidadOrganizacional ?? (typeof res?.data === 'number' ? res.data : null);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
      }

      // Manejar relación con empresa
      if (targetId && payload.idEmpresa) {
        try {
          // Buscar si ya existe la relación
          const resRel = await axios.get(ENDPOINTS.empresasUnidadesOrg);
          const allRels = (resRel.data || []).map(Models.empresasUnidadesOrg);
          const existing = allRels.find(r => r.idUnidadOrganizacional === targetId);

          const relPayload = {
            idEmpresa: payload.idEmpresa,
            idUnidadOrganizacional: targetId,
            activo: true
          };

          if (existing) {
            // Actualizar si cambió la empresa
            if (existing.idEmpresa !== payload.idEmpresa) {
              await axios.put(ENDPOINTS.empresasUnidadesOrg, {
                ...relPayload,
                idEmpresasUnidadesOrg: existing.idEmpresasUnidadesOrg
              });
            }
          } else {
            // Crear nueva relación
            await axios.post(ENDPOINTS.empresasUnidadesOrg, relPayload);
          }
        } catch (eRel) {
          console.error("Error updating relationship:", eRel);
        }
      }

      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`
      }
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button >
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por clave, nombre, zona, dirección o tipo…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Zona</th>
                  <th>Clave</th>
                  <th>Nombre</th>
                  <th>Tipo Unidad Org.</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idUnidadOrganizacional}>
                    <td><strong>{formatEmpresa(r)}</strong></td>
                    <td>{formatZona(r)}</td>
                    <td><strong>{normalizeValue(r.clave)}</strong></td>
                    <td>{normalizeValue(r.nombre)}</td>
                    <td>{formatTipoUnidadOrg(r)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => handleEdit(r.idUnidadOrganizacional)}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => {
                          askDelete(r.idUnidadOrganizacional, r.nombre || 'Unidad Org');
                        }}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={10}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
            {modalOpen && modalMode === 'edit' && selectedId > 0 && (
              <Tabs 
                unitId={selectedId} 
                onUpdate={loadData} 
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
